import { FC, useState } from 'react';

// material-ui imports
import { alpha, useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// other imports
import Avatar from 'ui-component/extended/Avatar';
import BlockTwoToneIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { format } from 'date-fns';
import { addCommasToNumbers, getBackgroundColor, getUserRegionFromStorage } from 'Application/utils';
import { usePolicyStore } from 'Application/store';
import { toast } from 'sonner';
import { GetAllPoliciesResponse } from 'Application/models';
import { ThemeMode } from 'config';
import { PushToAmis } from 'Application/components/Modals/PushToAims';
import CanclePolicy from 'Application/components/Modals/CancelPolicy';

import { useLocation, useNavigate } from 'react-router-dom';
import { rows } from 'views/forms/data-grid/DataGridBasic';

interface CompletedTransactionsListProps {
    policies: GetAllPoliciesResponse;
}

const CompletedTransactionsList: FC<CompletedTransactionsListProps> = ({ policies }) => {
    const theme = useTheme();
    const [downloading, setDownloading] = useState<string | null>(null);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const pathnameRole = pathname.split('/')[1];

    console.log(policies, 'CompletedTransactionsList');

    const { getPolicyDocument, isError, error } = usePolicyStore((state) => ({
        getPolicyDocument: state.getPolicyDocument,
        isError: state.isError,
        error: state.error
    }));

    const handleDownloadPolicy = async (policyNumber: string) => {
        setDownloading(policyNumber);
        try {
            toast.info('Downloading policy document...');
            const policyDocumentBlob = await getPolicyDocument(policyNumber);

            const url = window.URL.createObjectURL(policyDocumentBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Policy_${policyNumber}.pdf`;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success('Policy document downloaded successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to download the policy document.');
        } finally {
            setDownloading(null);
        }
    };
    const handleRedirect = (policyNumber: string) => {
        navigate(`/${pathnameRole}/travel-portal/completed-transactions/viewpolicy/${policyNumber}`);
    };
    const handleRedirectRwandaKyc = (policyNumber: string) => {
        navigate(`/${pathnameRole}/travel-portal/completed-transactions/viewpolicy-kyc-rw/${policyNumber}`);
    };
    const userRegion = getUserRegionFromStorage();
    const currency = userRegion === 'Kenya' ? 'Ksh' : 'RWF';

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Agency </TableCell>
                        <TableCell>Policy Number</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>{`Policy Amount (${currency})`}</TableCell>{' '}
                        <TableCell>{userRegion === 'Kenya' ? 'Passport Number' : 'Identification Number'}</TableCell>{' '}
                        <TableCell>Created At</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Aims Status</TableCell>
                        <TableCell>Aims Policy Number</TableCell>
                        <TableCell>Policy Status</TableCell>
                        {userRegion === 'Kenya' && (
                            <>
                                <TableCell>Payment Status</TableCell>
                                <TableCell>EVO Booking Status</TableCell>
                                <TableCell sx={{ minWidth: 150 }}>Cover edge policy code</TableCell>
                            </>
                        )}
                        <TableCell align="center" sx={{ pr: 3 }}>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {policies && Array.isArray(policies.content) && policies.content.length > 0 ? (
                        policies.content.map((row, index) => (
                            <TableRow hover key={index}>
                                <TableCell sx={{ pl: 3 }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar
                                            alt="User Avatar"
                                            className={`border border-gray-200 ${
                                                row.intermediaryName
                                                    ? getBackgroundColor(row.intermediaryName[0].toUpperCase())
                                                    : 'transparent'
                                            }`}
                                        >
                                            {row?.intermediaryName ? row.intermediaryName[0].toUpperCase() : ''}
                                        </Avatar>
                                        <Stack>
                                            <Typography variant="subtitle1">{row?.intermediaryName || ''}</Typography>
                                            <Typography variant="subtitle2" noWrap>
                                                {row?.intermediaryEmailAddress || ''}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </TableCell>
                                <TableCell>{row.policyNumber}</TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar
                                            alt="User Avatar"
                                            className={`border border-gray-200 ${
                                                row.customerName ? getBackgroundColor(row.customerName[0].toUpperCase()) : 'transparent'
                                            }`}
                                        >
                                            {row.customerName ? row.customerName[0].toUpperCase() : ''}
                                        </Avatar>
                                        <Stack>
                                            <Typography variant="subtitle1">{row.customerName || ''}</Typography>
                                            <Typography variant="subtitle2" noWrap>
                                                {row.customerEmailAddress || ''}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </TableCell>
                                <TableCell>{addCommasToNumbers(row.amount?.toString())}</TableCell>
                                <TableCell>{row.customerIdentificationNumber}</TableCell>
                                <TableCell>{row.createdAt ? format(new Date(row.createdAt), 'MM-dd-yyyy') : 'N/A'}</TableCell>
                                <TableCell>{row.issuerEmailAddress}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.policyTransactionStatus}
                                        size="small"
                                        sx={
                                            row.policyTransactionStatus === 'DEBITED'
                                                ? {
                                                      bgcolor:
                                                          theme.palette.mode === ThemeMode.DARK
                                                              ? 'dark.main'
                                                              : alpha(theme.palette.success.light, 0.6),
                                                      color: 'success.dark'
                                                  }
                                                : {
                                                      bgcolor:
                                                          theme.palette.mode === ThemeMode.DARK
                                                              ? 'dark.main'
                                                              : alpha(theme.palette.primary.light, 0.6),
                                                      color: 'warning.dark'
                                                  }
                                        }
                                    />
                                </TableCell>
                                <TableCell>{row.aimsPolicyNumber ?? '----'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.policyStatus}
                                        size="small"
                                        sx={
                                            row.policyStatus === 'CONFIRMED'
                                                ? {
                                                      bgcolor:
                                                          theme.palette.mode === ThemeMode.DARK
                                                              ? 'dark.main'
                                                              : alpha(theme.palette.success.light, 0.6),
                                                      color: 'success.dark'
                                                  }
                                                : {
                                                      bgcolor:
                                                          theme.palette.mode === ThemeMode.DARK
                                                              ? 'dark.main'
                                                              : alpha(theme.palette.primary.light, 0.6),
                                                      color: 'primary.dark'
                                                  }
                                        }
                                    />
                                </TableCell>
                                {userRegion === 'Kenya' && (
                                    <>
                                        <TableCell>
                                            <Chip
                                                label={row.isPaid ? 'PAID' : 'UNPAID'}
                                                size="small"
                                                sx={
                                                    row.isPaid
                                                        ? {
                                                              bgcolor:
                                                                  theme.palette.mode === ThemeMode.DARK
                                                                      ? 'dark.main'
                                                                      : alpha(theme.palette.success.light, 0.6),
                                                              color: 'success.dark'
                                                          }
                                                        : {
                                                              bgcolor:
                                                                  theme.palette.mode === ThemeMode.DARK
                                                                      ? 'dark.main'
                                                                      : alpha(theme.palette.primary.light, 0.6),
                                                              color: 'primary.dark'
                                                          }
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.isBookedOnEvo ? 'BOOKED' : 'NOT BOOKED'}
                                                size="small"
                                                sx={
                                                    row.isBookedOnEvo
                                                        ? {
                                                              bgcolor:
                                                                  theme.palette.mode === ThemeMode.DARK
                                                                      ? 'dark.main'
                                                                      : alpha(theme.palette.success.light, 0.6),
                                                              color: 'success.dark'
                                                          }
                                                        : {
                                                              bgcolor:
                                                                  theme.palette.mode === ThemeMode.DARK
                                                                      ? 'dark.main'
                                                                      : alpha(theme.palette.primary.light, 0.6),
                                                              color: 'primary.dark'
                                                          }
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{row.coverEdgePolicyCode ?? '----'}</TableCell>
                                    </>
                                )}
                                <TableCell align="center" sx={{ pr: 3 }}>
                                    <Stack direction="row" justifyContent="center" alignItems="center">
                                        {row.regionName === 'Kenya' ? (
                                            <Tooltip placement="top" title="View Policy">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleRedirect(row.policyNumber)} // Pass the policyNumber here
                                                    size="large"
                                                >
                                                    <PreviewIcon sx={{ fontSize: '1.1rem', ml: 1 }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : row.regionName === 'Rwanda' ? (
                                            <Tooltip placement="top" title="View Policy">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleRedirectRwandaKyc(row.policyNumber)} // Pass the policyNumber here
                                                    size="large"
                                                >
                                                    <PreviewIcon sx={{ fontSize: '1.1rem', ml: 1 }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}

                                        <PushToAmis policyNumber={row.policyNumber} />

                                        <CanclePolicy policyNumber={row.policyNumber} />

                                        <Tooltip placement="top" title="Download Policy Document">
                                            <IconButton
                                                color="success"
                                                onClick={() => handleDownloadPolicy(row.policyNumber)}
                                                disabled={downloading === row.policyNumber}
                                                sx={{
                                                    color: downloading === row.policyNumber ? 'grey.400' : 'orange.dark',
                                                    borderColor: 'orange.main',
                                                    '&:hover': { bgcolor: 'orange.light' }
                                                }}
                                                size="large"
                                            >
                                                <BlockTwoToneIcon sx={{ fontSize: '1.1rem', m: 1 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={10}>
                                <Typography variant="subtitle1" align="center">
                                    No policies available
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CompletedTransactionsList;
